/* eslint-disable react/jsx-handler-names */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';

import classNames from 'classnames';
import { get } from 'lodash';
import moment from 'moment';

import Delete from './Actions/Delete';
import Button from './Button';
import OptionalTextInput from './OptionalTextInput';
import LinkComponent from './Link';
import DocumentActions from './DocumentActions';
import Boolean from './Boolean';
import FetchableValue from './FetchableValue';
import Currency from './Currency';
import Checkbox from './Checkbox';
import Tooltip from '../../Tooltip';
import EditableWithButtons from './EditableWithButtons';
import List from './List';
import Badge from '../../Badge';
import DownloadDocButton from './DownloadDocButton';

export default function Row({ row, cols, compact, rowStyle, onRowClick }) {
  const shouldSpan = cols[0].fullRow && cols[0].fullRow(row);

  if (shouldSpan) {
    return (
      <tr className={row?.style ? row.style : ''}>
        <td colSpan={cols.length}>
          <div className={cols[0].spanColClassName ?? ''}>
            <span>{get(row, cols[0].keys[0])}</span>
          </div>
        </td>
      </tr>
    );
  }

  let style = '';

  // It is used to apply the style to the row
  if (row.style) {
    style = row.style;
  } else if (rowStyle) {
    style = typeof rowStyle === 'function' ? rowStyle(row) : rowStyle;
  }

  return (
    <tr
      className={style}
      onClick={() => {
        if (onRowClick) {
          onRowClick(row);
        }
      }}
    >
      {cols.map((col) => {
        let tdContent = null;

        const type = typeof col.type === 'function' ? col.type(row) : col.type;

        if (col.isColumnHidden) {
          return null;
        }

        if (col.isHidden && col.isHidden(row)) {
          return <td key={col.title} />;
        }

        if (type === 'delete') {
          tdContent = (
            <Delete
              onClick={() => col.onClick(row)}
              row={row}
              isVisibleHandler={col.isVisible}
            />
          );
        } else if (type === 'button') {
          tdContent = (
            <Button
              disabled={col.disabled ? col.disabled(row) : false}
              onClick={() => col.onClick(row)}
              label={col.label(row)}
              variant={col.buttonVariant?.(row)}
              width={col.width}
              isVisible={col.isVisible ? col.isVisible(row) : true}
            />
          );
        } else if (type === 'documentActions') {
          tdContent = (
            <DocumentActions
              doc={row}
              refetch={col.refetch}
              readOnly={col.readOnly}
              isLender={col.isLender}
              lenderId={col.lenderId}
            />
          );
        } else if (type === 'boolean') {
          tdContent = (
            <Boolean
              row={row}
              link={col.link}
              value={
                col.format
                  ? col.format(get(row, col.keys[0]), row)
                  : get(row, col.keys[0])
              }
            />
          );
        } else if (type === 'fetchableValue') {
          tdContent = (
            <FetchableValue
              link={col.link}
              fetchOptions={col.fetchOptions}
              value={get(row, col.keys[0])}
              row={row}
            />
          );
        } else if (type === 'currency') {
          tdContent = (
            <Currency
              row={row}
              compact={compact}
              value={
                col.format
                  ? col.format(get(row, col.keys?.[0]), row)
                  : get(row, col.keys?.[0])
              }
              link={col.link}
            />
          );
        } else if (type === 'optionalTextInput') {
          tdContent = (
            <OptionalTextInput
              compact={compact}
              isVisible={col.isVisible ? col.isVisible(row) : true}
              initialValue={
                col?.format
                  ? col.format(get(row, col.keys[0]), row)
                  : get(row, col.keys[0])
              }
              onChange={(value) => col.onChange?.(row, value)}
              inputType={col.inputType}
              readOnly={
                typeof col.readOnly === 'function'
                  ? col.readOnly(row)
                  : col.readOnly
              }
            />
          );
        } else if (type === 'editable') {
          const isEditable = col.isEditable(row);
          const value = col?.format
            ? col.format(get(row, col.keys[0]), row)
            : get(row, col.keys[0]);

          const defaultInput =
            col?.inputType === 'currency' ? (
              <Currency value={value} compact={compact} />
            ) : (
              <div
                className={classNames({
                  'text-[12px]': compact,
                })}
              >
                {value}
              </div>
            );

          tdContent = isEditable ? (
            <OptionalTextInput
              compact={compact}
              isVisible={col.isVisible ? col.isVisible(row) : true}
              initialValue={value}
              onChange={(val) => col.onChange?.(row, val)}
              inputType={col.inputType}
              readOnly={
                typeof col.readOnly === 'function'
                  ? col.readOnly(row)
                  : col.readOnly
              }
            />
          ) : (
            defaultInput
          );
        } else if (type === 'editableWithButtons') {
          tdContent = (
            <EditableWithButtons
              row={row}
              col={col}
              compact={compact}
              value={get(row, col.keys[0])}
              inputType={col.inputType}
              onSubmit={col.onSubmit}
              options={
                typeof col.options === 'function'
                  ? col.options(row)
                  : col.options
              }
            />
          );
        } else if (type === 'checkbox') {
          tdContent =
            col.format && col.format(get(row, col.keys[0]), row) ? (
              col.format(get(row, col.keys[0]), row)
            ) : (
              <Checkbox
                isSelected={col.isSelected?.(row)}
                onChange={() => col.onChange?.(row)}
                disabled={col.disabled?.(row)}
              />
            );
        } else if (type === 'date') {
          tdContent = (
            <div>
              {get(row, col.keys[0])
                ? moment(get(row, col.keys[0])).format('M/D/YY')
                : ''}
            </div>
          );
        } else if (type === 'component') {
          tdContent = <col.component {...col.props(row)} />;
        } else if (type === 'borrower') {
          const value = get(row, col.keys[0]);

          if (value) {
            const borrowerName =
              value.TIN_type === 'EIN'
                ? value.entity_name
                : `${value.first_name || ''} ${value.last_name || ''}`;

            tdContent = <div>{borrowerName}</div>;
          } else {
            tdContent = <div />;
          }
        } else if (type === 'badge') {
          let value = get(row, col.keys[0]);
          if (col.format) {
            value = col.format(value, row);
          }

          tdContent = (
            <Badge
              color={
                typeof col.badgeColor === 'function'
                  ? col.badgeColor(value)
                  : col.badgeColor
              }
              text={value}
              link={typeof col.link === 'function' ? col.link(row) : col.link}
            />
          );
        } else if (type === 'list') {
          tdContent = (
            <List
              compact={compact}
              value={get(row, col.keys[0])}
              link={col.link}
              row={row}
              createListItem={col.createListItem}
              createOnClick={col.createOnClick}
            />
          );
        } else if (type === 'downloadDocButton') {
          const docValue = get(row, col.keys[0]);
          // If the format function returns an object with docId and docName, use those
          if (col.format && typeof col.format === 'function') {
            const formattedValue = col.format(docValue, row);
            if (
              formattedValue &&
              typeof formattedValue === 'object' &&
              formattedValue.docId
            ) {
              tdContent = (
                <DownloadDocButton
                  docId={formattedValue.docId}
                  docName={formattedValue.docName}
                />
              );
            } else {
              tdContent = <DownloadDocButton docId={formattedValue} />;
            }
          } else {
            tdContent = <DownloadDocButton docId={docValue} />;
          }
        } else if (type === 'percentage') {
          const value = col.format
            ? col.format(get(row, col.keys[0]), row)
            : get(row, col.keys[0]);
          tdContent = (
            <div>{value ? `${(value * 100).toFixed?.(2)}%` : ''}</div>
          );
        } else {
          let value =
            col.keys?.length === 1
              ? get(row, col.keys[0])
              : col.keys?.map((key) => get(row, key)).join(' ');

          if (col.format) {
            value = col.format(value, row);
          }

          tdContent = col.link?.(row) ? (
            <LinkComponent
              value={value}
              col={col}
              row={row}
              isOuter={col.isOuter}
            />
          ) : (
            <div>{value}</div>
          );
        }

        return (
          <td
            data-row-id={row.id}
            data-value={get(row, col?.keys?.[0])}
            onClick={
              col.onTdClick ? (event) => col.onTdClick(event, row) : () => {}
            }
            key={col.title}
            className={classNames(
              {
                'px-2 first:pl-5 last:pr-5 py-3': !compact,
                'whitespace-nowrap': !col.wrapText,
                'px-2 text-[12px]': compact,
              },
              row.style
            )}
          >
            <div className="text-left flex items-center">
              {tdContent}
              {typeof col.tooltip === 'function' &&
                col.tooltip(row) &&
                get(row, col.keys[0]) && (
                  <div className="ml-2">
                    <Tooltip
                      position={col.tooltipPosition || 'right'}
                      fixed={col.fixedTooltip}
                      svgClassName={col.tooltipSvgClassName}
                      size={col.tooltipSize}
                    >
                      {col.tooltip(row)}
                    </Tooltip>
                  </div>
                )}
            </div>
          </td>
        );
      })}
    </tr>
  );
}
